import { SpanStatusCode, trace } from '@opentelemetry/api'

import {
  datadogParentIdFromOtelSpanId,
  datadogTraceIdFromOtelTraceId,
} from 'shared/otel/datadog-propagator'
import type { ApplicationError } from 'shared/utils/errors/application-error'

import type { ErrorReporter } from '../types'

/**
 * `toLogContext()` spreads arbitrary metadata, which can contain a circular
 * reference. Plain `JSON.stringify` would throw and cost us the whole line, so
 * cycles are replaced instead.
 */
function safeStringify(payload: Record<string, unknown>): string {
  const seen = new WeakSet<object>()

  return JSON.stringify(payload, (_key, value: unknown) => {
    if (typeof value !== 'object' || value === null) return value
    if (seen.has(value)) return '[Circular]'
    seen.add(value)
    return value
  })
}

/**
 * Server-side error reporter.
 *
 * Writes to two independent channels, because they answer different questions:
 *
 * 1. **The active OTel span** is marked errored, so the APM trace shows red and
 *    the failure is attributed to the request that caused it. Only possible
 *    when there is an active span.
 * 2. **A structured line on stdout**, which the log drain ships to Datadog. This
 *    is the durable record — it carries the stack and full context, and it works
 *    with no active span.
 *
 * Both channels are deliberately standards-based rather than SDK-based: OTel is
 * vendor-neutral, and structured stdout is the most portable sink there is. The
 * only Datadog-specific detail in this file is the `dd.*` attribute naming,
 * which is what lets Datadog join the log to its trace automatically.
 *
 * No browser imports, so this module is safe to load in any runtime.
 */
export class DatadogServerReporter implements ErrorReporter {
  report(error: ApplicationError): void {
    try {
      const span = trace.getActiveSpan()
      const spanContext = span?.spanContext()

      if (span) {
        span.recordException(error)
        span.setStatus({ code: SpanStatusCode.ERROR, message: error.message })
      }

      console.error(
        safeStringify({
          status: 'error',
          message: error.message,
          'error.kind': error.name,
          'error.message': error.message,
          'error.stack': error.stack,
          'error.fingerprint': error.fingerprint.join(':'),
          ...(spanContext
            ? {
                'dd.trace_id': datadogTraceIdFromOtelTraceId(
                  spanContext.traceId,
                ),
                'dd.span_id': datadogParentIdFromOtelSpanId(spanContext.spanId),
              }
            : {}),
          ...error.toLogContext(),
        }),
      )
    } catch (reporterError) {
      console.error('Datadog server reporter failed', {
        originalMessage: error?.message,
        stack: reporterError instanceof Error ? reporterError.stack : undefined,
      })
    }
  }

  /**
   * The Datadog-format trace id of the active request — matches the
   * `dd.trace_id` on the log line above, so support can pivot between them.
   */
  getCorrelationId(): string | undefined {
    try {
      const traceId = trace.getActiveSpan()?.spanContext().traceId
      return traceId ? datadogTraceIdFromOtelTraceId(traceId) : undefined
    } catch {
      return undefined
    }
  }
}
